// Selectable, accessible text over a rendered page (pdf.js style): one
// transparent, absolutely positioned span per text run, so the browser's own
// selection works on top of the bitmap. The spans sit inside elements that
// carry the document's structure for assistive technology (paragraphs,
// headings, lists, tables, figures with their alternative text, links,
// languages; spec §7.8); those elements take no space, except figures and
// links, which cover their area. Copying goes through selectionText(), which
// rebuilds the text from the runs' separators (spaces at line breaks, line
// breaks at paragraph/cell/box boundaries) instead of the browser's
// serialization of unrelated spans. A selection dragged off the text does
// not jump (see trackSelections). A selection from one cell of a table to
// another selects the rectangle of cells between them, which copy puts on
// the clipboard as tab-separated values and an HTML table (see selectCells).
import { Sep, type TextRun, type TextContent, type TextNode, type TextLink, type Rect } from "@bdfkit/core";
import { fontString } from "./resources.js";
import { hasExtent } from "./search.js";
import { tableCells, cellClipboard, type CellRange, type CellClipboard } from "./cells.js";

export interface TextLayerOptions {
  /** Class of the layer element (default "bdfTextLayer"). */
  className?: string;
  /**
   * Include ALT_TEXT runs; default true. They carry the real text of what was
   * drawn: ligatures and other glyphs mapped to private-use characters (placed
   * like any run), and text drawn as paths or child objects (placed at their
   * anchor along the baseline direction). Leaving them out drops that text
   * from selection and from screen readers.
   */
  altText?: boolean;
  /** Width measurement in CSS px for a CSS font string (default: a shared 2D context). */
  measure?: (font: string, text: string) => number;
  /**
   * Language of the layer (the first of the document's meta.dc.language; ""
   * for unknown). Set it:
   * the page around the layer is usually in the viewer's own language. Runs
   * in another language get their own lang attribute.
   */
  lang?: string;
  /**
   * Lay out cells outside tables (sheet tiles) as one table of rows sorted by
   * position, with the sheet's size and its header rows and columns (cells
   * whose reference says they are headers are headers too).
   */
  sheet?: { rows: number; cols: number; headerRows?: number; headerCols?: number };
  /** Accessible name of a link that covers no text (default: the URL, "page N" or "view ID"). */
  linkLabel?: (url: string) => string;
  /**
   * Called for the span of each run once it is placed, e.g. to keep the
   * cells of a sheet's frozen panes in place while the rest scrolls (with
   * z-index 2, above the other runs).
   */
  onSpan?: (span: HTMLSpanElement, run: TextRun) => void;
  /**
   * Whether the text can be selected (default true). A viewer that selects
   * the cells of a sheet itself turns it off; links still work, and screen
   * readers read the layer as before.
   */
  selectable?: boolean;
}

let sharedMeasure: ((font: string, text: string) => number) | undefined;
function defaultMeasure(): (font: string, text: string) => number {
  if (!sharedMeasure) {
    const ctx = document.createElement("canvas").getContext("2d")!;
    sharedMeasure = (font, text) => {
      ctx.font = font;
      return ctx.measureText(text).width;
    };
  }
  return sharedMeasure;
}

/**
 * Attribute names: on the run spans, on the layer element, on links to a page
 * or a view, on the element that ends a layer, on a layer and its end
 * element while a selection is dragged in it, on a layer whose text cannot
 * be selected, and on a table whose cells are selected and the element that
 * shows them.
 */
export const RUN_ATTR = {
  ordinal: "data-bdf-ordinal", sep: "data-bdf-sep", layer: "data-bdf-layer", page: "data-bdf-page", view: "data-bdf-view", end: "data-bdf-end",
  selecting: "data-bdf-selecting", noselect: "data-bdf-noselect", cells: "data-bdf-cells", cellBox: "data-bdf-cellbox",
} as const;

/**
 * A link within the document: "#page=N" (a page of the same view) or
 * "#view=ID" with an optional "&page=N" (another view, e.g. another page
 * of a diagram), spec §7.7. Pages are 1-based.
 */
export interface InternalLink { view?: string; page?: number }

/** Parse an internal link url; undefined for anything else. */
export function internalLink(url: string): InternalLink | undefined {
  const page = /^#page=(\d{1,7})$/.exec(url);
  if (page) return Number(page[1]) >= 1 ? { page: Number(page[1]) } : undefined;
  const view = /^#view=([^&#]+)(?:&page=(\d{1,7}))?$/.exec(url);
  if (!view) return undefined;
  let id: string;
  try {
    id = decodeURIComponent(view[1].replace(/\+/g, " "));
  } catch {
    return undefined;
  }
  if (view[2] !== undefined && Number(view[2]) < 1) return undefined;
  return view[2] !== undefined ? { view: id, page: Number(view[2]) } : { view: id };
}

/**
 * The href of a LINK url, or undefined when a link must not be made: only
 * http, https and mailto URLs, "#page=N" and "#view=ID" (spec §7.7).
 */
export function linkHref(url: string): string | undefined {
  if (url.startsWith("#")) return internalLink(url) ? url : undefined;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" || u.protocol === "mailto:" ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/** An element and the layer position (CSS px) that its absolutely positioned children are placed from. */
interface Placed { el: HTMLElement; ox: number; oy: number }

/**
 * Build the text layer of one page, band or sheet region: the extractor's
 * runs in reading order, or its content (runs with their structure and
 * links), in page units; scale is CSS px per unit. The element must be
 * placed over the page (position: absolute; inset: 0) and needs
 * TEXT_LAYER_CSS.
 */
export function buildTextLayer(input: TextRun[] | TextContent, scale: number, opts: TextLayerOptions = {}): HTMLDivElement {
  const layer = document.createElement("div");
  layer.className = opts.className ?? "bdfTextLayer";
  layer.setAttribute(RUN_ATTR.layer, "");
  if (opts.selectable === false) layer.setAttribute(RUN_ATTR.noselect, "");
  if (opts.lang !== undefined) layer.lang = opts.lang;
  const content: TextContent = Array.isArray(input) ? { runs: input, nodes: [], links: [] } : input;
  new LayerBuilder(layer, content, scale, opts).build();
  const end = document.createElement("div");
  end.setAttribute(RUN_ATTR.end, "");
  end.setAttribute("aria-hidden", "true");
  layer.appendChild(end);
  trackSelections();
  return layer;
}

// A selection dragged off the text. Over empty space (a margin, the space
// around a page) the browser resolves the pointer to a position far away,
// typically the start of the page, as the runs are absolutely positioned:
// the selection jumps or collapses. While a selection is dragged, as in
// pdf.js, the end element of the layer where it ends covers the scroll area
// under the runs (which have z-index 1), and it moves in the DOM next to the
// run where the selection ends: before it when the selection runs backward.
// Over empty space the pointer is on that element, which cannot be selected:
// the selection stays where it is, or at most reaches the element, taking in
// the rest of that run. Firefox keeps the selection where it is over such an
// element, so there it stays at the end of its layer (as in pdf.js). Outside
// the scroll area the browser's own dragging goes on, which scrolls it.

/** Whether a button is down (mouse or pen), and the end element that covers the scroll area. */
const drag: { down: boolean; end: HTMLElement | undefined } = { down: false, end: undefined };
let tracking = false;
let gecko: boolean | undefined;
/** The run spans of each layer, in document order. */
const layerRuns = new WeakMap<Element, HTMLElement[]>();

function trackSelections() {
  if (tracking || typeof document === "undefined") return;
  tracking = true;
  document.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    drag.down = true;
    // pressed on a run: cover before the drag leaves it
    const run = e.target instanceof Element ? e.target.closest<HTMLElement>(`[${RUN_ATTR.ordinal}]`) : null;
    const layer = run?.closest(`[${RUN_ATTR.layer}]`);
    if (run && layer && !layer.hasAttribute(RUN_ATTR.noselect)) cover(layer, run, false);
  }, true);
  const up = () => {
    drag.down = false;
    uncover();
  };
  document.addEventListener("pointerup", up, true);
  document.addEventListener("pointercancel", up, true);
  window.addEventListener("blur", up);
  document.addEventListener("selectionchange", () => {
    follow();
    selectCells();
  });
}

/** Keep the cover next to the end of the selection that moves. */
function follow() {
  if (!drag.down) return;
  const sel = getSelection();
  if (!sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  // a press off the runs covers once the selection reaches them
  if (range.collapsed && !drag.end) return;
  const back = !range.collapsed && sel.focusNode === range.startContainer && sel.focusOffset === range.startOffset;
  const node = back ? range.startContainer : range.endContainer;
  const offset = back ? range.startOffset : range.endOffset;
  const layer = (node instanceof Element ? node : node.parentElement)?.closest(`[${RUN_ATTR.layer}]`);
  if (!layer) return;
  const run = runAt(layer, node, offset, back);
  if (run) cover(layer, run, back);
}

/**
 * The run a boundary point of the selection is in or next to, on the side
 * the selection covers: the last run starting before an end, the first
 * ending after a start.
 */
function runAt(layer: Element, node: Node, offset: number, start: boolean): HTMLElement | undefined {
  let runs = layerRuns.get(layer);
  if (!runs) layerRuns.set(layer, (runs = [...layer.querySelectorAll<HTMLElement>(`[${RUN_ATTR.ordinal}]`)]));
  const at = document.createRange();
  at.setStart(node, offset);
  // runs are in document order: a binary search for the first run past the point
  const past = (run: HTMLElement) => {
    const text = run.firstChild!;
    return start ? at.comparePoint(text, text.textContent!.length) > 0 : at.comparePoint(text, 0) >= 0;
  };
  let lo = 0, hi = runs.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (past(runs[mid])) hi = mid;
    else lo = mid + 1;
  }
  return start ? runs[lo] : runs[lo - 1];
}

/** Show a layer's end element over the scroll area, next to a run (before it, or after it). */
function cover(layer: Element, run: HTMLElement, before: boolean) {
  const end = layer.querySelector<HTMLElement>(`[${RUN_ATTR.end}]`);
  if (!end) return;
  if (drag.end !== end) {
    uncover();
    drag.end = end;
    const r = scrollArea(layer);
    end.style.cssText = `left: ${r.x}px; top: ${r.y}px; width: ${r.w}px; height: ${r.h}px`;
    end.setAttribute(RUN_ATTR.selecting, "");
    layer.setAttribute(RUN_ATTR.selecting, "");
  }
  gecko ??= typeof CSS !== "undefined" && CSS.supports("-moz-user-select", "none");
  if (gecko || (before ? run.previousSibling : run.nextSibling) === end) return;
  run.parentNode!.insertBefore(end, before ? run : run.nextSibling);
}

/** Put the end element back at the end of its layer, hidden. */
function uncover() {
  const end = drag.end;
  if (!end) return;
  drag.end = undefined;
  end.removeAttribute(RUN_ATTR.selecting);
  end.style.cssText = "";
  const layer = end.closest(`[${RUN_ATTR.layer}]`);
  if (!layer) return;
  layer.removeAttribute(RUN_ATTR.selecting);
  if (layer.lastChild !== end) layer.appendChild(end);
}

/** The visible area of the element that scrolls the layer (the window when none does), in CSS px of the window. */
function scrollArea(layer: Element): { x: number; y: number; w: number; h: number } {
  for (let el = layer.parentElement; el && el !== document.documentElement && el !== document.body; el = el.parentElement) {
    const s = getComputedStyle(el);
    if (!/auto|scroll|overlay/.test(`${s.overflowX} ${s.overflowY}`)) continue;
    const r = el.getBoundingClientRect();
    return { x: r.left + el.clientLeft, y: r.top + el.clientTop, w: el.clientWidth, h: el.clientHeight };
  }
  return { x: 0, y: 0, w: document.documentElement.clientWidth, h: document.documentElement.clientHeight };
}

// Cells of a table. A selection dragged from one cell of a table into
// another (or extended there with the keys) selects the rectangle of cells
// between them, as in a word processor or a spreadsheet: a box shows it
// instead of the browser's highlight of the runs, and copy puts the cells on
// the clipboard (selectionCells). A selection within one cell, or going out
// of the table, stays a text selection. The box covers the text of the rows
// and columns selected, up to the text of the next ones (the layer does not
// know where cells end). A layer whose text cannot be selected keeps none of
// this.

/** A cell of a table in a layer, with the bounding box of its text (CSS px in the layer). */
interface LayerCell { row: number; col: number; rows: number; cols: number; box?: Rect }

/** A table of a layer: its node (-1 for the cells of a sheet), and its cells that hold text. */
interface LayerTable {
  layer: HTMLElement;
  content: TextContent;
  node: number;
  keep: (run: TextRun) => boolean;
  cells: LayerCell[];
  /** Font size of its first run (CSS px), for the margin of the box. */
  size: number;
}

const layerTables = new WeakMap<Element, LayerTable>();
const layerCells = new WeakMap<Element, LayerCell>();

/** The cells selected, and the element that shows them. */
let cellSelection: { table: Element; info: LayerTable; range: CellRange; box: HTMLElement } | undefined;

/** The tables around a node, innermost first, each with the cell of it that holds the node. */
function cellsAround(node: Node | null): Map<Element, LayerCell> {
  const out = new Map<Element, LayerCell>();
  let cell: LayerCell | undefined;
  for (let el = node instanceof Element ? node : node?.parentElement ?? null; el; el = el.parentElement) {
    cell = layerCells.get(el) ?? cell;
    if (layerTables.has(el)) {
      if (cell) out.set(el, cell);
      cell = undefined;
    }
    if (el.hasAttribute(RUN_ATTR.layer)) break;
  }
  return out;
}

/** The table whose cells a selection selects: its ends lie in two cells of it. */
function selectedTable(sel: Selection): { table: Element; info: LayerTable; range: CellRange } | undefined {
  if (sel.isCollapsed || !sel.rangeCount) return undefined;
  const from = cellsAround(sel.anchorNode);
  if (!from.size) return undefined;
  for (const [table, to] of cellsAround(sel.focusNode)) {
    const start = from.get(table);
    if (!start) continue;
    if (start === to) return undefined;
    const info = layerTables.get(table)!;
    return { table, info, range: cellRange(info, start, to) };
  }
  return undefined;
}

/** The rectangle of cells between two cells, grown to take in the merged cells it cuts. */
function cellRange(info: LayerTable, a: LayerCell, b: LayerCell): CellRange {
  const r: CellRange = {
    row0: Math.min(a.row, b.row), col0: Math.min(a.col, b.col),
    row1: Math.max(a.row + a.rows, b.row + b.rows) - 1, col1: Math.max(a.col + a.cols, b.col + b.cols) - 1,
  };
  for (let grew = true; grew;) {
    grew = false;
    for (const c of info.cells) {
      if (c.rows === 1 && c.cols === 1) continue;
      if (c.row > r.row1 || c.row + c.rows - 1 < r.row0 || c.col > r.col1 || c.col + c.cols - 1 < r.col0) continue;
      const next = { row0: Math.min(r.row0, c.row), col0: Math.min(r.col0, c.col), row1: Math.max(r.row1, c.row + c.rows - 1), col1: Math.max(r.col1, c.col + c.cols - 1) };
      if (next.row0 !== r.row0 || next.col0 !== r.col0 || next.row1 !== r.row1 || next.col1 !== r.col1) {
        Object.assign(r, next);
        grew = true;
      }
    }
  }
  return r;
}

/**
 * Where a rectangle of cells lies (CSS px in the layer): across the text of
 * every cell within its columns, and down the text of every cell within its
 * rows, so that the edges of a column are the same on every row, with a
 * margin. The right and bottom edges go on to the text of the next column
 * and row: cells end there when their text starts at their left and top,
 * as it usually does.
 */
function rangeBox(info: LayerTable, r: CellRange): Rect | undefined {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, next = Infinity, below = Infinity;
  for (const c of info.cells) {
    const b = c.box;
    if (!b) continue;
    if (c.col >= r.col0 && c.col + c.cols - 1 <= r.col1) { x0 = Math.min(x0, b.x); x1 = Math.max(x1, b.x + b.w); }
    if (c.row >= r.row0 && c.row + c.rows - 1 <= r.row1) { y0 = Math.min(y0, b.y); y1 = Math.max(y1, b.y + b.h); }
    if (c.col > r.col1) next = Math.min(next, b.x);
    if (c.row > r.row1) below = Math.min(below, b.y);
  }
  if (x0 > x1 || y0 > y1) return undefined;
  const m = info.size * 0.3;
  x1 = next < Infinity ? Math.max(x1 + m, next - m) : x1 + m;
  y1 = below < Infinity ? Math.max(y1 + m, below - m) : y1 + m;
  return { x: x0 - m, y: y0 - m, w: x1 - x0 + m, h: y1 - y0 + m };
}

/** Follow the selection: show the cells it selects, or stop showing them. */
function selectCells() {
  const sel = getSelection();
  const found = sel ? selectedTable(sel) : undefined;
  const cur = cellSelection;
  if (cur && found && cur.table === found.table && sameRange(cur.range, found.range)) return;
  if (cur) {
    cur.box.remove();
    cur.table.removeAttribute(RUN_ATTR.cells);
    cellSelection = undefined;
  }
  if (!found) return;
  const box = document.createElement("div");
  box.setAttribute(RUN_ATTR.cellBox, "");
  box.setAttribute("aria-hidden", "true");
  const b = rangeBox(found.info, found.range);
  if (b) box.style.cssText = `left: ${b.x}px; top: ${b.y}px; width: ${b.w}px; height: ${b.h}px`;
  found.info.layer.prepend(box);
  found.table.setAttribute(RUN_ATTR.cells, "");
  cellSelection = { ...found, box };
}

const sameRange = (a: CellRange, b: CellRange) => a.row0 === b.row0 && a.col0 === b.col0 && a.row1 === b.row1 && a.col1 === b.col1;

/** Bounding box of a run's text in its space: the span's box before the layer's scale. */
function runBox(r: TextRun, measure: (font: string, text: string) => number): Rect {
  let m: readonly number[] = r.matrix, x0 = 0, x1: number;
  const size = r.size > 0 ? r.size : 10;
  if (hasExtent(r)) {
    const width = r.advance > 0 ? r.advance : measure(fontString(r.font!, r.size), r.text);
    x0 = r.align === 1 ? -width : r.align === 2 ? -width / 2 : 0;
    x1 = x0 + width;
  } else {
    // only the anchor and the baseline direction are known (as the span is laid)
    const angle = Math.atan2(m[1], m[0]);
    m = [Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle)];
    x1 = size * r.text.length * 0.5;
  }
  let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
  for (const dx of [x0, x1]) for (const dy of [-size * 0.8, size * 0.2]) {
    const px = m[0] * dx + m[2] * dy + r.x, py = m[1] * dx + m[3] * dy + r.y;
    bx0 = Math.min(bx0, px); by0 = Math.min(by0, py); bx1 = Math.max(bx1, px); by1 = Math.max(by1, py);
  }
  return { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 };
}

class LayerBuilder {
  private readonly root: Placed;
  private readonly measure: (font: string, text: string) => number;
  private readonly placed = new Map<number, Placed>();
  /** Current row of each table element. */
  private readonly rows = new Map<HTMLElement, { row: number; el: HTMLElement }>();
  /** Link covering each run (-1: none). */
  private linkOf: number[] = [];
  /** Whether each link is one a link may be made of (linkHref). */
  private linked: boolean[] = [];
  private textLinks = new Set<number>();
  /** The anchor the previous run went into, to continue it. */
  private anchor: { parent: HTMLElement; link: number; placed: Placed } | undefined;
  /** The runs placed in the layer. */
  private readonly keep: (r: TextRun) => boolean;
  /** The table cell each node lies in (null: none). */
  private readonly cellOf = new Map<number, { cell: LayerCell; table: LayerTable } | null>();

  constructor(layer: HTMLElement, private c: TextContent, private scale: number, private opts: TextLayerOptions) {
    this.root = { el: layer, ox: 0, oy: 0 };
    this.measure = opts.measure ?? defaultMeasure();
    this.keep = (r) => !!r.text && !(r.altText && opts.altText === false);
  }

  build() {
    const { runs, nodes, links } = this.c;
    const keep = this.keep;
    // once a link, not once a run and link: a page of links has many of both
    this.linked = links.map((l) => linkHref(l.url) !== undefined);
    this.linkOf = runs.map((r) => (keep(r) ? this.coveringLink(r) : -1));
    for (const l of this.linkOf) if (l >= 0) this.textLinks.add(l);
    if (this.opts.sheet) this.sheetTable();
    // Nodes are created in stream order and a run always belongs to the newest
    // node, so walking both in step keeps the DOM in reading order: figures
    // without text are placed where they were drawn, other nodes on demand.
    let nextNode = 0, nextLink = 0;
    const standalone = (upTo: number) => {
      for (; nextLink < links.length && links[nextLink].after < upTo; nextLink++) {
        if (!this.textLinks.has(nextLink)) this.standaloneLink(nextLink);
      }
    };
    runs.forEach((r, i) => {
      for (; r.node !== undefined && nextNode <= r.node && nextNode < nodes.length; nextNode++) this.figure(nextNode);
      standalone(i);
      if (keep(r)) this.run(r, i);
    });
    for (; nextNode < nodes.length; nextNode++) this.figure(nextNode);
    standalone(Infinity);
  }

  /** Place a figure that has alternative text even when it covers no text. */
  private figure(i: number) {
    const n = this.c.nodes[i];
    if (n.kind === "figure" && n.alt) this.node(i);
  }

  /** The element of a node, created with its ancestors on first use. */
  private node(i: number | undefined): Placed {
    if (i === undefined || i < 0 || i >= this.c.nodes.length) return this.root;
    const got = this.placed.get(i);
    if (got) return got;
    const n = this.c.nodes[i];
    const parent = this.node(n.parent);
    const parentNode = n.parent >= 0 ? this.c.nodes[n.parent] : undefined;
    let placed: Placed;
    if (n.kind === "cell" && parentNode?.kind === "table") {
      placed = { ...parent, el: this.cell(n, this.row(parent.el, n.row ?? 0), n.scope) };
      this.tableCell(placed.el, n, parent.el);
    } else if (n.kind === "cell") {
      // a sheet cell outside sheet mode (sheetTable places the others)
      placed = { ...parent, el: this.div(parent.el, "paragraph") };
    } else if (n.kind === "figure") {
      const b = n.bounds;
      if (n.alt && b) {
        const el = this.div(parent.el, "img");
        el.setAttribute("aria-label", n.alt);
        placed = this.position(el, parent, b.x, b.y, b.w, b.h);
      } else {
        placed = { ...parent, el: this.div(parent.el) }; // decorative: no role
      }
    } else {
      const el = this.div(parent.el, ROLE[n.kind]);
      if (n.kind === "heading" && n.level) el.setAttribute("aria-level", String(n.level));
      if (n.kind === "table") {
        this.tableSize(el, i);
        this.table(el, i);
      }
      placed = { ...parent, el };
    }
    this.placed.set(i, placed);
    return placed;
  }

  private div(parent: HTMLElement, role?: string): HTMLElement {
    const el = document.createElement("div");
    if (role) el.setAttribute("role", role);
    parent.appendChild(el);
    return el;
  }

  /** Absolutely position el over a rectangle (page units); returns it as the origin of its children. */
  private position(el: HTMLElement, parent: Placed, x: number, y: number, w: number, h: number): Placed {
    const s = this.scale;
    el.style.left = `${x * s - parent.ox}px`;
    el.style.top = `${y * s - parent.oy}px`;
    el.style.width = `${w * s}px`;
    el.style.height = `${h * s}px`;
    return { el, ox: x * s, oy: y * s };
  }

  /** Register a table element for selecting its cells. */
  private table(el: HTMLElement, node: number) {
    if (this.opts.selectable === false) return;
    layerTables.set(el, { layer: this.root.el, content: this.c, node, keep: this.keep, cells: [], size: 0 });
  }

  /** Register the element of a cell of a table. */
  private tableCell(el: HTMLElement, n: TextNode, table: HTMLElement) {
    const info = layerTables.get(table);
    if (!info) return;
    const cell: LayerCell = { row: n.row ?? 0, col: n.col ?? 0, rows: n.rows ?? 1, cols: n.cols ?? 1 };
    layerCells.set(el, cell);
    info.cells.push(cell);
  }

  /** Grow the box of the table cell a run lies in by the run's. */
  private cellText(r: TextRun) {
    if (this.opts.selectable === false) return;
    const at = this.cellAt(r.node);
    if (!at) return;
    const b = runBox(r, this.measure), s = this.scale;
    const box = { x: b.x * s, y: b.y * s, w: b.w * s, h: b.h * s };
    const c = at.cell.box;
    at.cell.box = c ? { x: Math.min(c.x, box.x), y: Math.min(c.y, box.y), w: Math.max(c.x + c.w, box.x + box.w) - Math.min(c.x, box.x), h: Math.max(c.y + c.h, box.y + box.h) - Math.min(c.y, box.y) } : box;
    at.table.size ||= (r.size > 0 ? r.size : 10) * s;
  }

  /** The table cell a node lies in: the nearest ancestor placed as one. */
  private cellAt(i: number | undefined): { cell: LayerCell; table: LayerTable } | null {
    if (i === undefined || i < 0 || i >= this.c.nodes.length) return null;
    let got = this.cellOf.get(i);
    if (got === undefined) {
      const el = this.placed.get(i)?.el;
      const cell = el && layerCells.get(el);
      const table = cell && el.closest("[role=table]");
      const info = table && layerTables.get(table);
      got = cell && info ? { cell, table: info } : this.cellAt(this.c.nodes[i].parent);
      this.cellOf.set(i, got);
    }
    return got;
  }

  private tableSize(el: HTMLElement, table: number) {
    let rows = 0, cols = 0;
    for (const n of this.c.nodes) {
      if (n.parent !== table || n.kind !== "cell") continue;
      rows = Math.max(rows, (n.row ?? 0) + (n.rows ?? 1));
      cols = Math.max(cols, (n.col ?? 0) + (n.cols ?? 1));
    }
    if (rows) el.setAttribute("aria-rowcount", String(rows));
    if (cols) el.setAttribute("aria-colcount", String(cols));
  }

  /** The row element for a cell starting at row; a new one when the row changes (cells are never reordered). */
  private row(table: HTMLElement, row: number): HTMLElement {
    const cur = this.rows.get(table);
    if (cur && cur.row === row) return cur.el;
    const el = this.div(table, "row");
    el.setAttribute("aria-rowindex", String(row + 1));
    this.rows.set(table, { row, el });
    return el;
  }

  private cell(n: TextNode, row: HTMLElement, scope: "col" | "row" | undefined): HTMLElement {
    const el = this.div(row, scope === "col" ? "columnheader" : scope === "row" ? "rowheader" : "cell");
    el.setAttribute("aria-colindex", String((n.col ?? 0) + 1));
    if ((n.rows ?? 1) > 1) el.setAttribute("aria-rowspan", String(n.rows));
    if ((n.cols ?? 1) > 1) el.setAttribute("aria-colspan", String(n.cols));
    return el;
  }

  /** Sheet mode: one table of the cells outside tables, rows sorted by position. */
  private sheetTable() {
    const sheet = this.opts.sheet!;
    const cells: number[] = [];
    const used = new Set(this.c.runs.map((r) => r.node));
    this.c.nodes.forEach((n, i) => {
      if (n.kind === "cell" && n.parent < 0 && n.row !== undefined && used.has(i)) cells.push(i);
    });
    if (!cells.length) return;
    const table = this.div(this.root.el, "table");
    table.setAttribute("aria-rowcount", String(sheet.rows));
    table.setAttribute("aria-colcount", String(sheet.cols));
    this.table(table, -1);
    const at = (i: number) => this.c.nodes[i];
    cells.sort((a, b) => at(a).row! - at(b).row! || at(a).col! - at(b).col!);
    for (const i of cells) {
      const n = at(i);
      // a header of its own (" col" after the reference: a table's header row), or the frozen rows and columns
      const scope = n.scope ?? (n.row! < (sheet.headerRows ?? 0) ? "col" : n.col! < (sheet.headerCols ?? 0) ? "row" : undefined);
      const el = this.cell(n, this.row(table, n.row!), scope);
      this.tableCell(el, n, table);
      this.placed.set(i, { ...this.root, el });
    }
  }

  /** The smallest link whose rectangle contains the middle of a run (-1: none). */
  private coveringLink(r: TextRun): number {
    const links = this.c.links;
    if (!links.length) return -1;
    const w = r.advance > 0 ? r.advance : r.size * r.text.length * 0.5;
    const cx = (r.align === 1 ? -w / 2 : r.align === 2 ? 0 : w / 2), cy = -r.size * 0.3;
    const m = r.matrix;
    const x = m[0] * cx + m[2] * cy + r.x, y = m[1] * cx + m[3] * cy + r.y;
    let best = -1;
    links.forEach((l, i) => {
      if (x < l.x || x > l.x + l.w || y < l.y || y > l.y + l.h || !this.linked[i]) return;
      if (best < 0 || l.w * l.h < links[best].w * links[best].h) best = i;
    });
    return best;
  }

  private anchorFor(link: TextLink, parent: Placed): Placed {
    const a = document.createElement("a");
    const href = linkHref(link.url)!;
    a.href = href;
    a.draggable = false;
    const internal = internalLink(href);
    if (internal) {
      if (internal.view !== undefined) a.setAttribute(RUN_ATTR.view, internal.view);
      if (internal.page !== undefined) a.setAttribute(RUN_ATTR.page, String(internal.page));
    } else {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
    parent.el.appendChild(a);
    return this.position(a, parent, link.x, link.y, link.w, link.h);
  }

  private standaloneLink(i: number) {
    const link = this.c.links[i];
    if (!this.linked[i]) return;
    const placed = this.anchorFor(link, this.node(link.node));
    const internal = internalLink(link.url);
    const fallback = internal?.view !== undefined ? `view ${internal.view}` : internal?.page !== undefined ? `page ${internal.page}` : link.url;
    placed.el.setAttribute("aria-label", this.opts.linkLabel?.(link.url) ?? fallback);
  }

  private run(r: TextRun, i: number) {
    let target = this.node(r.node);
    const link = this.linkOf[i];
    if (link >= 0) {
      // consecutive runs of one leaf under the same link share an anchor
      const a = this.anchor;
      if (a && a.parent === target.el && a.link === link && a.placed.el === target.el.lastElementChild) target = a.placed;
      else {
        const placed = this.anchorFor(this.c.links[link], target);
        this.anchor = { parent: target.el, link, placed };
        target = placed;
      }
    }
    const span = this.span(r, target);
    target.el.appendChild(span);
    this.cellText(r);
    this.opts.onSpan?.(span, r);
  }

  private span(r: TextRun, at: Placed): HTMLSpanElement {
    const span = document.createElement("span");
    span.textContent = r.text;
    span.setAttribute(RUN_ATTR.ordinal, String(r.ordinal));
    span.setAttribute(RUN_ATTR.sep, String(r.sep));
    if (r.lang && r.lang !== (this.opts.lang ?? "")) span.lang = r.lang;
    const scale = this.scale;
    if (hasExtent(r)) {
      const font = fontString(r.font!, r.size);
      span.style.font = font;
      // The main thread may not have the embedded font: stretch the fallback
      // rendering to the advance the worker measured with the real one.
      const w = this.measure(font, r.text);
      const sx = r.advance > 0 && w > 0 ? r.advance / w : 1;
      const width = r.advance > 0 ? r.advance : w;
      const anchor = r.align === 1 ? -width : r.align === 2 ? -width / 2 : 0;
      const m = r.matrix;
      span.style.transform = `matrix(${m[0] * scale}, ${m[1] * scale}, ${m[2] * scale}, ${m[3] * scale}, ${r.x * scale - at.ox}, ${r.y * scale - at.oy}) translate(${anchor}px, ${-r.size * 0.8}px) scaleX(${sx})`;
    } else {
      // ALT_TEXT for paths, a child object or no drawing op: only the anchor
      // and the baseline direction are known, so lay the text there.
      const size = (r.size > 0 ? r.size : 10) * scale;
      const angle = Math.atan2(r.matrix[1], r.matrix[0]);
      span.style.font = `${size}px sans-serif`;
      span.style.transform = `translate(${r.x * scale - at.ox}px, ${r.y * scale - at.oy}px) rotate(${angle}rad) translate(0, ${-size * 0.8}px)`;
    }
    // The font shorthand set the line height as well, to normal, over the 1
    // of the layer: the run's box would be as high as a line of the font the
    // browser falls back to (1.5 em for some), and its text that much lower
    // in it than what was drawn. A style sheet cannot undo that.
    span.style.lineHeight = "1";
    return span;
  }
}

const ROLE: Record<TextNode["kind"], string> = {
  paragraph: "paragraph", heading: "heading", caption: "caption", list: "list", item: "listitem",
  table: "table", cell: "cell", figure: "img",
};

/**
 * CSS that a text layer needs; the class names follow TextLayerOptions.className.
 * forced-color-adjust keeps the text transparent in forced colors (Windows
 * high contrast), which would otherwise paint it opaque over the canvas.
 * Figures and links are positioned boxes; a figure lets pointer events
 * through to what lies under it, except on its text. Runs have z-index 1, so
 * that they stay above the end element, which covers the scroll area while a
 * selection is dragged; that also lets links through then, except their text.
 * A run meant to lie above others (a cell of a frozen pane) needs z-index 2.
 * The text of a layer that cannot be selected takes the cursor of what lies
 * around it. Selected cells are one box under the runs, which then do not
 * show the browser's highlight.
 */
export const TEXT_LAYER_CSS = `
.bdfTextLayer { position: absolute; inset: 0; overflow: hidden; line-height: 1; forced-color-adjust: none; }
.bdfTextLayer span { position: absolute; z-index: 1; color: transparent; white-space: pre; transform-origin: 0 0; cursor: text; }
.bdfTextLayer span::selection { background: rgba(0, 120, 255, .35); }
.bdfTextLayer [role=img], .bdfTextLayer a { position: absolute; }
.bdfTextLayer [role=img] { pointer-events: none; }
.bdfTextLayer [role=img] span { pointer-events: auto; }
.bdfTextLayer a, .bdfTextLayer a span { cursor: pointer; -webkit-user-drag: none; }
.bdfTextLayer a:focus-visible { outline: 2px solid #1a73e8; outline-offset: 1px; }
.bdfTextLayer [data-bdf-end] { display: none; position: fixed; inset: 0; z-index: 0; cursor: text; -webkit-user-select: none; user-select: none; }
.bdfTextLayer [data-bdf-end][data-bdf-selecting] { display: block; }
.bdfTextLayer[data-bdf-selecting] a { pointer-events: none; }
.bdfTextLayer[data-bdf-selecting] a span { pointer-events: auto; }
.bdfTextLayer[data-bdf-noselect] span { -webkit-user-select: none; user-select: none; cursor: inherit; }
.bdfTextLayer [data-bdf-cells] span::selection { background: transparent; }
.bdfTextLayer [data-bdf-cellbox] { position: absolute; z-index: 0; pointer-events: none; background: rgba(0, 120, 255, .18); outline: 2px solid rgba(0, 100, 230, .8); }
`;

/** A selected piece of a run. */
export interface SelectedRun {
  ordinal: number;
  sep: number;
  text: string;
  /** Element holding the run; layers differ when the selection spans pages. */
  layer: Element | null;
}

/**
 * The runs (or parts of runs) a DOM range covers, in document order: every
 * run span under root that intersects the range, clipped to it.
 */
export function selectedRuns(range: Range, root: ParentNode = document): SelectedRun[] {
  const out: SelectedRun[] = [];
  for (const span of root.querySelectorAll<HTMLElement>(`[${RUN_ATTR.ordinal}]`)) {
    const node = span.firstChild;
    if (!node || !range.intersectsNode(span)) continue;
    const full = node.textContent ?? "";
    let start = 0, end = full.length;
    // The range starts inside this run when its first character lies before the
    // range start; the start offset is only meaningful within the text node.
    if (range.comparePoint(node, 0) < 0) start = range.startContainer === node ? range.startOffset : 0;
    if (range.comparePoint(node, full.length) > 0) end = range.endContainer === node ? range.endOffset : full.length;
    if (end <= start) continue;
    out.push({ ordinal: Number(span.getAttribute(RUN_ATTR.ordinal)), sep: Number(span.getAttribute(RUN_ATTR.sep)), text: full.slice(start, end), layer: span.closest(`[${RUN_ATTR.layer}]`) });
  }
  return out;
}

/**
 * Join selected runs into plain text: Sep.SPACE becomes a space, Sep.BREAK a
 * line break, and a change of layer (page) a blank line. The first run's
 * own separator is dropped.
 */
export function joinRuns(parts: SelectedRun[]): string {
  let s = "";
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (i > 0) {
      if (p.layer !== parts[i - 1].layer) s += "\n\n";
      else if (p.sep === Sep.BREAK) s += "\n";
      else if (p.sep === Sep.SPACE) s += " ";
    }
    s += p.text;
  }
  return s;
}

/** Plain text of the current selection within root (empty when nothing is selected there). */
export function selectionText(sel: Selection | null = typeof getSelection === "function" ? getSelection() : null, root: ParentNode = document): string {
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return "";
  let s = "";
  for (let i = 0; i < sel.rangeCount; i++) {
    const t = joinRuns(selectedRuns(sel.getRangeAt(i), root));
    if (t) s = s ? `${s}\n${t}` : t;
  }
  return s;
}

/**
 * The cells the selection selects within root, when it goes from one cell
 * of a table to another: the rectangle of cells between them as
 * tab-separated values and an HTML table (what spreadsheets paste as cells).
 * Undefined for a text selection.
 */
export function selectionCells(sel: Selection | null = typeof getSelection === "function" ? getSelection() : null, root: Node = document): CellClipboard | undefined {
  const found = sel ? selectedTable(sel) : undefined;
  if (!found || !root.contains(found.table)) return undefined;
  const { info, range } = found;
  return cellClipboard(tableCells(info.content, info.node, info.keep), range);
}

/**
 * Make copy (and cut) inside container put the selection's run text on the
 * clipboard instead of the browser's serialization, and selected cells of a
 * table as tab-separated values and an HTML table. Returns a function that
 * removes the handler.
 */
export function installCopyHandler(container: HTMLElement): () => void {
  const onCopy = (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    let cells: CellClipboard | undefined;
    try {
      cells = selectionCells(getSelection(), container);
    } catch {
      // more cells than are copied as cells: the text of the runs is
    }
    if (cells) {
      e.clipboardData.setData("text/plain", cells.text);
      e.clipboardData.setData("text/html", cells.html);
      e.preventDefault();
      return;
    }
    const text = selectionText(getSelection(), container);
    if (!text) return;
    e.clipboardData.setData("text/plain", text);
    e.preventDefault();
  };
  container.addEventListener("copy", onCopy);
  container.addEventListener("cut", onCopy);
  return () => {
    container.removeEventListener("copy", onCopy);
    container.removeEventListener("cut", onCopy);
  };
}
